<template>
  <div class="rb-ob-form rb-match-page rb-book-page">
    <template v-if="person">
      <header>
        <h1 class="rb-ob-title">{{ $t('intake.holdTitle') }}</h1>
      </header>

      <div class="rb-book-therapist">
        <span class="rb-match-therapist-avatar" aria-hidden="true">{{ person.initial }}</span>
        <div class="rb-book-therapist-meta">
          <p class="rb-book-therapist-name">{{ therapistDisplayName }}</p>
          <p class="rb-book-therapist-role">
            <template v-if="therapistDisplayTitle">{{ therapistDisplayTitle }} · </template>{{ $t('intake.verifiedTherapist') }}
          </p>
        </div>
      </div>

      <div class="rb-book-fee">
        <span class="rb-book-fee-label">{{ $t('intake.sessionOnlineHour') }}</span>
        <span class="rb-book-fee-value">
          {{ displaySessionPrice }}
          {{ $t('intake.slotsPriceSuffix') }}
        </span>
      </div>

      <div class="rb-book-hold-timer" role="timer" aria-live="polite">
        <p class="rb-book-hold-clock" dir="ltr">{{ holdLabel }}</p>
        <p class="rb-book-hold-hint">{{ $t('intake.holdHint') }}</p>
      </div>

      <ul class="rb-book-summary" :aria-label="$t('intake.holdSummaryAria')">
        <li>
          <span class="rb-book-summary-k">{{ $t('intake.holdSummaryTime') }}</span>
          <span class="rb-book-summary-v">{{ summaryTime }}</span>
        </li>
        <li>
          <span class="rb-book-summary-k">{{ $t('intake.holdSummaryTz') }}</span>
          <span class="rb-book-summary-v">{{ $t('intake.slotsTzValue') }}</span>
        </li>
        <li>
          <span class="rb-book-summary-k">{{ $t('intake.holdSummaryAmount') }}</span>
          <span class="rb-book-summary-v">
            {{ displaySessionPrice }}
            {{ $t('intake.slotsPriceSuffix') }}
          </span>
        </li>
        <li>
          <span class="rb-book-summary-k">{{ $t('intake.holdSummaryPayMethod') }}</span>
          <span class="rb-book-summary-v">{{ $t('intake.holdPayMethodValue') }}</span>
        </li>
      </ul>

      <div class="rb-ob-actions">
        <button class="rb-ob-submit" type="button" :disabled="holdSeconds <= 0" @click="continuePay">
          {{ $t('intake.holdContinuePay') }}
        </button>
        <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="changeTime">
          {{ $t('intake.holdChangeTime') }}
        </button>
        <p class="rb-ob-status" role="status" aria-live="polite"></p>
        <div class="rb-ob-trust">
          <p>{{ $t('intake.trustPath') }}</p>
          <p>{{ $t('authUi.familyNote') }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  loadIntake,
  saveIntake,
  hasSafetyRisk,
  isIntakeReadyToMatch,
  toFaDigits,
} from '@/intake/intakeState'
import {formatMoneyForDisplay, priceTierLabel} from '@/intake/formatMoney'
import {formatSlotDateTime} from '@/intake/formatSlotDateTime'
import {
  getMockMatch,
  HOLD_SECONDS,
  formatBookingSummary,
} from '@/intake/mockMatches'
import {isSandboxMatchEnabled} from '@/intake/sandboxMatchApi'
import {getSessionPerson, hasSandboxMatchSession, loadMatchSession} from '@/intake/sandboxMatchSession'

export default {
  name: 'MatchHoldStep',
  data() {
    return {
      person: null,
      holdSeconds: HOLD_SECONDS,
      timerId: null,
    }
  },
  computed: {
    holdLabel() {
      const m = Math.floor(this.holdSeconds / 60)
      const s = this.holdSeconds % 60
      const raw = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
      return this.$i18n.locale === 'fa' ? this.toFaDigits(raw) : raw
    },
    summaryTime() {
      const draft = loadIntake()
      const lang = this.$i18n.locale === 'en' ? 'en' : 'fa'
      if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
        const session = loadMatchSession() || {}
        if (session.selectedSlotDateLabel && session.selectedSlotTimeLabel) {
          return session.selectedSlotDateLabel + ' · ' + session.selectedSlotTimeLabel
        }
        if (session.selectedSlotLabel && !String(session.selectedSlotLabel).includes('T')) {
          return session.selectedSlotLabel
        }
        const formatted = formatSlotDateTime(draft.bookingTime || session.selectedSlotLabel, lang)
        if (formatted.combinedLabel) {
          return formatted.combinedLabel
        }
      }
      if (draft.bookingTime && String(draft.bookingTime).includes('T')) {
        const formatted = formatSlotDateTime(draft.bookingTime, lang)
        if (formatted.combinedLabel) {
          return formatted.combinedLabel
        }
      }
      return formatBookingSummary(draft.bookingDate, draft.bookingTime, this.$i18n.locale)
    },
    therapistDisplayName() {
      if (!this.person) {
        return ''
      }
      if (this.person.name) {
        return this.person.name
      }
      if (this.person.sandbox && /^t\d+$/i.test(String(this.person.id))) {
        return this.$t('intake.therapist.' + this.person.id + '.name')
      }
      return ''
    },
    therapistDisplayTitle() {
      if (!this.person) {
        return ''
      }
      if (this.person.title && !/^tier_\d+$/i.test(this.person.title)) {
        return this.person.title
      }
      if (this.person.tier) {
        return priceTierLabel(this.person.tier, this.$route.params.lang || 'fa')
      }
      if (this.person.sandbox && /^t\d+$/i.test(String(this.person.id))) {
        return this.$t('intake.therapist.' + this.person.id + '.title')
      }
      return ''
    },
    displaySessionPrice() {
      if (!this.person) {
        return ''
      }
      const money = formatMoneyForDisplay(this.person.price, this.person.currency)
      if (money.isToman || money.unit === 'raw') {
        return money.text + ' ' + this.$t('intake.toman')
      }
      if (money.currency) {
        return money.text + ' ' + money.currency
      }
      return money.text
    },
  },
  created() {
    if (isSandboxMatchEnabled()) {
      this.$router.replace({name: 'intakeCheckout', params: {lang: this.$route.params.lang || 'fa'}})
      return
    }
    const draft = loadIntake()
    if (!isIntakeReadyToMatch(draft)) {
      this.$router.replace({name: 'intakeReview'})
      return
    }
    if (hasSafetyRisk(draft.safety)) {
      this.$router.replace({name: 'intakeCrisis'})
      return
    }
    if (!draft.bookingDate || !draft.bookingTime || !draft.therapistId) {
      this.$router.replace({name: 'intakeMatches'})
      return
    }

    let person = null
    if (isSandboxMatchEnabled() && hasSandboxMatchSession()) {
      person = getSessionPerson(draft.therapistId)
    }
    if (!person) {
      person = getMockMatch(draft.therapistId)
    }
    if (!person) {
      this.$router.replace({name: 'intakeMatches'})
      return
    }
    this.person = person

    const started = Number(draft.holdStartedAt) || Date.now()
    if (!draft.holdStartedAt) {
      saveIntake({holdStartedAt: started})
    }
    const elapsed = Math.floor((Date.now() - started) / 1000)
    this.holdSeconds = Math.max(0, HOLD_SECONDS - elapsed)
    if (this.holdSeconds <= 0) {
      this.$router.replace({name: 'intakeHoldExpired'})
      return
    }
    this.timerId = setInterval(this.tick, 1000)
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId)
    }
  },
  methods: {
    toFaDigits,
    tick() {
      if (this.holdSeconds <= 0) {
        return
      }
      this.holdSeconds -= 1
      if (this.holdSeconds <= 0) {
        clearInterval(this.timerId)
        this.timerId = null
        this.$router.replace({name: 'intakeHoldExpired'})
      }
    },
    continuePay() {
      this.$router.push({
        name: 'intakeCheckout',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
    changeTime() {
      const draft = loadIntake()
      this.$router.push({
        name: 'intakeSlots',
        params: {
          lang: this.$route.params.lang || 'fa',
          therapistId: draft.therapistId || this.person.id,
        },
      })
    },
  },
}
</script>
